import { useState } from "react";
import TextField from "@mui/material/TextField";
import {
  COVERAGE_GAP_NAME_MAX_LENGTH,
  COVERAGE_GAP_NOTE_MAX_LENGTH,
} from "@shared/types/corpus/api.ts";
import { FormDialog } from "../../../../components/primitives/feedback/FormDialog/FormDialog";
import { useFormDialog } from "../../../../components/primitives/feedback/FormDialog/useFormDialog";

export interface CoverageGapFormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  submitLabel: string;
  /** What the form starts with; its name and note when a gap is edited. */
  initial?: { name: string; note: string };
  /** Called with the name and note when the person confirms. Throw to surface an error and keep
   *  the dialog open. */
  onSubmit: (name: string, note: string) => Promise<void>;
}

/** Collects the name and the note of a coverage gap, to create one or to edit one. Give it a `key`
 *  that changes with the gap edited, so the form starts from that gap. */
export function CoverageGapFormDialog({
  open,
  onClose,
  title,
  submitLabel,
  initial = { name: "", note: "" },
  onSubmit,
}: CoverageGapFormDialogProps) {
  const [name, setName] = useState(initial.name);
  const [note, setNote] = useState(initial.note);

  const { dialogProps } = useFormDialog({
    onSubmit: async () => {
      await onSubmit(name.trim(), note.trim());
    },
    onClose: () => {
      setName(initial.name);
      setNote(initial.note);
      onClose();
    },
  });

  return (
    <FormDialog
      open={open}
      title={title}
      submitLabel={submitLabel}
      maxWidth="sm"
      submitDisabled={name.trim().length === 0}
      {...dialogProps}
    >
      <TextField
        label="Name"
        size="small"
        fullWidth
        required
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        slotProps={{ htmlInput: { maxLength: COVERAGE_GAP_NAME_MAX_LENGTH } }}
        placeholder="What the corpus lacks, e.g. Seed oils and inflammation"
      />
      <TextField
        label="Note"
        size="small"
        fullWidth
        multiline
        minRows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        slotProps={{ htmlInput: { maxLength: COVERAGE_GAP_NOTE_MAX_LENGTH } }}
        placeholder="What the claims have in common, what to look for…"
      />
    </FormDialog>
  );
}
